class ONTheme {
  constructor(root = document) {
    this.init(root);
  }

  init(root = document) {
    this.bindMenu(root);
    this.bindProductGalleries(root);
    this.bindProductForms(root);
    this.bindQuantityUpdates(root);
    this.bindFilters(root);
    this.bindSubmitStates(root);
    this.bindAppointmentDates(root);
    this.bindConfirmations(root);
    this.focusFormStatus(root);
  }

  bindProductGalleries(root = document) {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.bound === 'true') return;
      gallery.dataset.bound = 'true';

      const thumbnails = [...gallery.querySelectorAll('[data-gallery-thumbnail]')];
      const mediaItems = [...gallery.querySelectorAll('[data-gallery-media]')];
      const counter = gallery.querySelector('[data-gallery-counter]');
      if (!thumbnails.length || !mediaItems.length) return;

      const activateMedia = (mediaId, moveFocus = false) => {
        const selectedIndex = thumbnails.findIndex((thumbnail) => thumbnail.dataset.mediaId === String(mediaId));
        if (selectedIndex < 0) return;

        thumbnails.forEach((thumbnail, index) => {
          const isActive = index === selectedIndex;
          thumbnail.classList.toggle('is-active', isActive);
          thumbnail.setAttribute('aria-pressed', String(isActive));
          thumbnail.tabIndex = isActive ? 0 : -1;
          if (isActive && moveFocus) thumbnail.focus();
        });

        mediaItems.forEach((item) => {
          const isActive = item.dataset.mediaId === String(mediaId);
          item.hidden = !isActive;
          if (!isActive) item.querySelectorAll('video').forEach((video) => video.pause());
        });

        if (counter) counter.textContent = `${selectedIndex + 1} / ${thumbnails.length}`;
      };

      thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => activateMedia(thumbnail.dataset.mediaId));
        thumbnail.addEventListener('keydown', (event) => {
          let nextIndex = index;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % thumbnails.length;
          else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + thumbnails.length) % thumbnails.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = thumbnails.length - 1;
          else return;
          event.preventDefault();
          activateMedia(thumbnails[nextIndex].dataset.mediaId, true);
        });
      });

      gallery.closest('[data-product-root]')?.addEventListener('variant:change', (event) => {
        if (event.detail?.variant?.featured_media_id) activateMedia(event.detail.variant.featured_media_id);
      });
    });
  }

  bindMenu(root = document) {
    root.querySelectorAll('[data-menu-toggle]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';

      const menu = document.getElementById(button.getAttribute('aria-controls'));
      if (!menu) return;

      const closeMenu = (returnFocus = false) => {
        menu.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('menu-open');
        if (returnFocus) button.focus();
      };

      button.addEventListener('click', () => {
        const open = !menu.classList.contains('is-open');
        menu.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('menu-open', open);
      });

      menu.addEventListener('click', (event) => {
        if (event.target.closest('a')) closeMenu();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu.classList.contains('is-open')) closeMenu(true);
      });
    });
  }

  bindProductForms(root = document) {
    root.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';

      const variantSelect = form.querySelector('[data-variant-id]');
      const optionSelects = [...form.querySelectorAll('[data-option-position]')];
      const variantsNode = form.querySelector('[data-variants-json]');
      const submit = form.querySelector('[name="add"]');
      const price = form.closest('[data-product-root]')?.querySelector('[data-product-price]');
      let variants = [];

      try {
        variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
      } catch (error) {
        console.warn('ON\' Elegancia : données de variantes invalides.', error);
        if (submit) submit.disabled = true;
        return;
      }

      if (variantSelect) variantSelect.disabled = false;

      const updateVariant = () => {
        const choices = optionSelects.map((select) => select.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === choices[index]));

        if (variantSelect) variantSelect.value = variant?.id || '';
        if (submit) {
          submit.disabled = !variant || !variant.available;
          submit.textContent = !variant ? submit.dataset.unavailable : variant.available ? submit.dataset.add : submit.dataset.soldOut;
        }
        if (price && variant) price.textContent = variant.price_text;

        if (variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
          form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
        }
      };

      optionSelects.forEach((select) => select.addEventListener('change', updateVariant));
      if (optionSelects.length) updateVariant();
    });
  }

  bindQuantityUpdates(root = document) {
    root.querySelectorAll('[data-quantity-button]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const input = document.getElementById(button.getAttribute('aria-controls'));
        if (!input) return;
        const minimum = Number(input.min || 0);
        const maximum = Number(input.max || Number.POSITIVE_INFINITY);
        const current = Number(input.value || minimum);
        const next = Math.min(maximum, Math.max(minimum, current + Number(button.dataset.step || 0)));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  bindFilters(root = document) {
    root.querySelectorAll('[data-filter-form] input, [data-filter-form] select').forEach((input) => {
      if (input.dataset.bound === 'true') return;
      input.dataset.bound = 'true';
      input.addEventListener('change', () => input.form?.requestSubmit());
    });
  }

  bindSubmitStates(root = document) {
    root.querySelectorAll('form').forEach((form) => {
      if (form.dataset.submitBound === 'true' || form.matches('[data-filter-form]')) return;
      form.dataset.submitBound = 'true';
      form.addEventListener('submit', (event) => {
        const submitter = event.submitter || form.querySelector('button[type="submit"], input[type="submit"]');
        if (!submitter) return;
        submitter.setAttribute('aria-busy', 'true');
        submitter.classList.add('is-loading');
        window.setTimeout(() => {
          submitter.removeAttribute('aria-busy');
          submitter.classList.remove('is-loading');
        }, 8000);
      });
    });
  }

  bindAppointmentDates(root = document) {
    root.querySelectorAll('[data-min-today]').forEach((input) => {
      const now = new Date();
      const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      input.min = localDate;
      if (input.value && input.value < localDate) input.value = '';
    });
  }

  bindConfirmations(root = document) {
    root.querySelectorAll('[data-confirm]').forEach((control) => {
      if (control.dataset.confirmBound === 'true') return;
      control.dataset.confirmBound = 'true';
      control.addEventListener('click', (event) => {
        if (!window.confirm(control.dataset.confirm)) event.preventDefault();
      });
    });
  }

  focusFormStatus(root = document) {
    const status = root.querySelector('.form-status[tabindex="-1"]');
    if (status) status.focus({ preventScroll: true });
  }
}

const initTheme = () => {
  window.ONThemeInstance = new ONTheme();
};

document.addEventListener('DOMContentLoaded', initTheme);
document.addEventListener('shopify:section:load', (event) => {
  if (!window.ONThemeInstance) window.ONThemeInstance = new ONTheme(event.target);
  else window.ONThemeInstance.init(event.target);
});
